/**
 * `OpsRecorder` — a host's session as handle-ops records: the SDK verbs it
 * called through a facade (`row.update`, `cells.write`, `rows.delete`, …)
 * and what the user did to the view (`view.scroll`, `view.goTo`,
 * `view.setConfig`, `prefs.set`, `nav.open`, `nav.back`), in the one op
 * vocabulary `lona --dev sheets.ops` replays (`.ops.json`, or one record
 * per JSONL line for `sheets.tui --mutate -`).
 *
 * The host records at its seams, beside the call it makes (behind a debug
 * flag, say): a write is recorded with the row's
 * label as it was when the call was made, so a later rename is replayed in
 * order. A facade change is one `via` record. Consecutive scrolls in one
 * direction with nothing between them are one `view.scroll`. Dates are
 * written in the ops' one spelling: `YYYY-MM-DD` for a day, a partial
 * date's own text for a period.
 *
 * DOM-free: it holds JSON records and reads nothing from the page.
 *
 * Replay: `lona --dev sheets.ops --bundle <bundle.json> --ops <recording.ops.json>`
 * (a bundle from `sheets.dump`), or `--fixture`.
 *
 * @module
 */

import type { NaiveDate } from "@tento-chrono";
import { type Json, type JsonObject, type Option, Patch, type RendererTz, type RowPatch } from "@tento-lona/sheets";
import type { CellTarget } from "@tento-lona";

/** A host's session as handle-ops records (module doc). */
export class OpsRecorder {
  readonly #ops: OpsRecorder.Op[] = [];
  #via: Option<OpsRecorder.Facade> = null;
  /** Columns of a scroll not yet recorded (coalesced while the direction holds). */
  #scroll = 0;

  // ── Facade writes ──────────────────────────────────────────────────

  /** `row.update` (`mode` `durable`: `updateDurable`) of the row labelled `rowLabel`, through `via`. */
  rowUpdate(via: OpsRecorder.Facade, rowLabel: string, patch: RowPatch, mode: OpsRecorder.Mode): void {
    this.#write(via, { op: "row.update", rowLabel, patch: OpsRecorder.patchJson(patch), ...durableOf(mode) });
  }

  /** `row.mergeAttributes`. */
  rowMergeAttributes(via: OpsRecorder.Facade, rowLabel: string, patch: JsonObject): void {
    this.#write(via, { op: "row.mergeAttributes", rowLabel, patch });
  }

  /** A joined alias row's `setProp`. */
  rowSetProp(rowLabel: string, name: string, value: Json): void {
    this.#write("joined", { op: "row.setProp", rowLabel, name, value });
  }

  /** `cells.write` (`mode` `durable`: `writeDurable`). */
  cellsWrite(via: OpsRecorder.Facade, rowLabel: string, target: CellTarget, value: Json, mode: OpsRecorder.Mode): void {
    this.#write(via, { op: "cells.write", rowLabel, target: OpsRecorder.targetJson(target), value, ...durableOf(mode) });
  }

  /** `cells.clear` (`mode` `durable`: `clearDurable`). */
  cellsClear(via: OpsRecorder.Facade, rowLabel: string, target: CellTarget, mode: OpsRecorder.Mode): void {
    this.#write(via, { op: "cells.clear", rowLabel, target: OpsRecorder.targetJson(target), ...durableOf(mode) });
  }

  /** `handle.rows.delete` of the row labelled `rowLabel` (the core handle's verb). */
  rowsDelete(rowLabel: string): void {
    this.#write("handle", { op: "rows.delete", rowLabel });
  }

  /** The undo a deletion notice offers. */
  undo(): void {
    this.#push({ op: "undo" });
  }

  /** `client.sheets.update(handle, { title })` (`mode` `durable`: the debounced rename). */
  sheetsRename(title: Option<string>, mode: OpsRecorder.Mode): void {
    this.#write("handle", { op: "sheets.rename", title, ...durableOf(mode) });
  }

  // ── View gestures ──────────────────────────────────────────────────

  /** A scroll of `columns` columns (negative: back in time), one per column crossing. */
  viewScroll(columns: number): void {
    if (columns === 0) return;
    if (this.#scroll !== 0 && Math.sign(columns) !== Math.sign(this.#scroll)) this.#flushScroll();
    this.#scroll += columns;
  }

  /** A go-to-date: the view re-anchored on `date`. */
  viewGoTo(date: NaiveDate): void {
    this.#push({ op: "view.goTo", date: date.toString() });
  }

  /** A view-config switch that applied. */
  viewSetConfig(timescale: string, count: Option<number>, tzType: Option<RendererTz>): void {
    this.#push({ op: "view.setConfig", timescale, ...(count != null ? { count } : {}), ...(tzType != null ? { tzType } : {}) });
  }

  /** A preference change, by its localStorage key and the value stored there. */
  prefsSet(key: string, value: Json): void {
    this.#push({ op: "prefs.set", key, value });
  }

  /** A navigation to another sheet, by the key it opened. */
  navOpen(sheetKey: string): void {
    this.#push({ op: "nav.open", sheetKey });
  }

  /** A navigation back to the sheet before. */
  navBack(): void {
    this.#push({ op: "nav.back" });
  }

  // ── Output ─────────────────────────────────────────────────────────

  /** Every record so far, in call order. */
  get ops(): readonly OpsRecorder.Op[] {
    this.#flushScroll();
    return [...this.#ops];
  }

  /** One record per line (`sheets.tui --mutate -` reads this). */
  toJsonl(): string {
    return this.ops.map((op) => `${JSON.stringify(op)}\n`).join("");
  }

  /**
   * The recording as an `.ops.json` for `sheets.ops`: the handle tier over a
   * sheet the server holds (`open: "synced"`) or a client-only one.
   */
  toOpsFile(description: string, open: "synced" | "ephemeral"): JsonObject {
    return { description, tier: { open }, ops: [...this.ops] };
  }

  #write(via: OpsRecorder.Facade, op: OpsRecorder.Op): void {
    if (via !== this.#via) {
      this.#push({ op: "via", facade: via });
      this.#via = via;
    }
    this.#push(op);
  }

  #push(op: OpsRecorder.Op): void {
    this.#flushScroll();
    this.#ops.push(op);
  }

  #flushScroll(): void {
    if (this.#scroll === 0) return;
    const columns = this.#scroll;
    this.#scroll = 0;
    this.#ops.push({ op: "view.scroll", columns });
  }
}

/** `OpsRecorder`'s record shapes and the conversions a seam records with. */
export namespace OpsRecorder {
  /** The facade a write goes through: the core handle, the joined tree's handle, or the rows-side model. */
  export type Facade = "handle" | "joined" | "rows";

  /** How a write was sent: `plain` (`write`, `update`, …), or `durable` (`writeDurable`, `updateDurable`, …). */
  export type Mode = "plain" | "durable";

  /** One handle-ops record: the op's name and its JSON fields. */
  export type Op = { readonly op: string; readonly [field: string]: Json };

  /** A day as `YYYY-MM-DD`, a period as its partial-date text (`ym-2026-4`): the ops' one date spelling. */
  export function dateText(date: NaiveDate.Partial): string {
    return date.type === "day" ? date.start.toString() : date.toString();
  }

  /** A `CellTarget` as the record's `target` (`date`, `add`, `timepoint` or `range`). */
  export function targetJson(target: CellTarget): JsonObject {
    switch (target.kind) {
      case "date":
        return { kind: "date", date: dateText(target.date) };
      case "add":
        return { kind: "add", date: dateText(target.date) };
      case "add-with-id":
        return { kind: "add", date: dateText(target.date), id: target.id.toString() };
      case "dated-cell":
        return target.cell.id == null
          ? { kind: "date", date: dateText(target.cell.date) }
          : { kind: "add", date: dateText(target.cell.date), id: target.cell.id.toString() };
      case "timepoint-cell":
        return { kind: "timepoint", id: target.cell.id.toString() };
      case "range":
        return { kind: "range", start: target.range.start.toString(), end: target.range.end.toString() };
    }
  }

  /** A `RowPatch` as the record's `patch`: a cleared field is `null`. */
  export function patchJson(patch: RowPatch): JsonObject {
    const out: { [field: string]: Json } = {};
    if (patch.label !== undefined) out["label"] = Patch.wire(patch.label);
    if (patch.icon !== undefined) out["icon"] = Patch.wire(patch.icon);
    if (patch.heightPx !== undefined) out["heightPx"] = Patch.wire(patch.heightPx);
    if (patch.formula !== undefined) out["formula"] = Patch.wire(patch.formula);
    if (patch.timeScale !== undefined) out["timeScale"] = patch.timeScale;
    if (patch.attributes !== undefined) out["attributes"] = patch.attributes;
    return out;
  }
}

/** A record's `durable` field: present (`true`) for a durable write only. */
function durableOf(mode: OpsRecorder.Mode): { readonly [field: string]: Json } {
  return mode === "durable" ? { durable: true } : {};
}
